import { useEffect, useState } from "react";
import { Navigate, useSearchParams } from "react-router";
import { useAuth } from "../context/AuthContext";
import { useLocale } from "../context/LanguageContext";
import { useMutation, gql } from "@apollo/client";
import { startOidcWeb, OidcStartError, type OidcProvider } from "../lib/auth";
import { apolloErrorCode } from "../lib/apolloError";
import { SsoProviderButton } from "../components/ui/SsoProviderButton";

const REQUEST_MAGIC_LINK = gql`
  mutation RequestMagicLink($input: MagicLinkInput!) {
    requestMagicLink(input: $input) {
      success
      message
      error
    }
  }
`;

/** SSO login is rolled out behind the same flag as the org SSO config UI. */
const SSO_ENABLED = import.meta.env.VITE_FEATURE_SSO_OIDC === "true";

export function LoginPage() {
  const { isAuthenticated, isLoading } = useAuth();
  const { t } = useLocale();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ssoError, setSsoError] = useState<string | null>(null);
  // Org enforces SSO → hide magic link, surface the SSO buttons as primary.
  const [enforced, setEnforced] = useState(false);
  const [redirectingProvider, setRedirectingProvider] =
    useState<OidcProvider | null>(null);
  const [requestMagicLink, { loading }] = useMutation(REQUEST_MAGIC_LINK);

  // The web SSO landing page bounces here with ?error=sso_failed when it could
  // not establish a session from the refresh cookie. Surface it generically.
  useEffect(() => {
    if (searchParams.get("error") === "sso_failed") {
      setSsoError(t("auth.sso.sessionFailed"));
    }
  }, [searchParams, t]);

  // Redirect if already authenticated
  if (isAuthenticated && !isLoading) {
    return <Navigate to="/" replace />;
  }

  /** Map an OIDC stable code to a locale message (reuses oidc.error.codes.*). */
  function oidcCodeMessage(code: string): string {
    // Rate-limit hit on the shared /auth bucket — tell the user to wait,
    // don't show a generic "unexpected error".
    if (code === "RATE_LIMITED") return t("auth.sso.tooManyAttempts");
    const key = `oidc.error.codes.${code}`;
    const resolved = t(key);
    return resolved === key ? t("auth.sso.startFailed") : resolved;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSsoError(null);

    const trimmed = email.trim().toLowerCase();
    if (!trimmed) return;

    try {
      const result = await requestMagicLink({
        variables: {
          input: {
            email: trimmed,
            redirectUrl: `${window.location.origin}/verify`,
          },
        },
      });

      const payload = result.data?.requestMagicLink;
      if (payload?.error) {
        console.error("[LoginPage] requestMagicLink error:", payload.error);
        setError(t("auth.sendFailed"));
        return;
      }

      setSent(true);
    } catch (err) {
      const code = apolloErrorCode(err);
      // Org enforces SSO — pivot the UI instead of showing a raw error. No
      // provider hint is revealed (anti-enumeration): both buttons are shown.
      if (code === "SSO_ENFORCED") {
        setEnforced(true);
        setError(null);
        return;
      }
      if (code === "SIGN_IN_NOT_ELIGIBLE") {
        setError(t("auth.sso.notEligible"));
        return;
      }
      console.error("[LoginPage] requestMagicLink failed:", err);
      setError(t("auth.sendFailed"));
    }
  }

  async function handleSso(provider: OidcProvider) {
    setError(null);
    setSsoError(null);
    setRedirectingProvider(provider);
    try {
      const authorizationUrl = await startOidcWeb(provider);
      // Full-page navigation to the IdP — this unloads the SPA.
      window.location.assign(authorizationUrl);
    } catch (err) {
      const code = err instanceof OidcStartError ? err.code : "INTERNAL_ERROR";
      setSsoError(oidcCodeMessage(code));
      setRedirectingProvider(null);
    }
  }

  if (sent) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="w-full max-w-sm rounded-lg border border-border bg-card p-8 text-center shadow-sm">
          <div className="mb-4 text-4xl">📧</div>
          <h1 className="mb-2 text-xl font-bold text-foreground">
            {t("auth.checkEmail")}
          </h1>
          <p className="mb-4 text-sm text-muted-foreground">
            {t("auth.magicLinkSent", { email })}
          </p>
          <p className="text-xs text-muted-foreground">
            {t("auth.magicLinkExpiry")}
          </p>
          <button
            type="button"
            onClick={() => {
              setSent(false);
              setEmail("");
            }}
            className="mt-6 text-sm font-medium text-primary hover:underline"
          >
            {t("auth.useDifferentEmail")}
          </button>
        </div>
      </div>
    );
  }

  const isRedirecting = redirectingProvider !== null;

  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="w-full max-w-sm rounded-lg border border-border bg-card p-8 shadow-sm">
        <h1 className="mb-2 text-2xl font-bold text-foreground">
          {t("auth.appTitle")}
        </h1>
        <p className="mb-6 text-sm text-muted-foreground">
          {t("auth.signInSubtitle")}
        </p>

        {enforced && (
          <div
            role="alert"
            className="mb-6 rounded-lg border border-border bg-muted p-4"
          >
            <h2 className="mb-1 text-sm font-semibold text-foreground">
              {t("auth.sso.enforcedTitle")}
            </h2>
            <p className="text-xs text-muted-foreground">
              {t("auth.sso.enforcedBody")}
            </p>
          </div>
        )}

        {ssoError && (
          <p role="alert" className="mb-4 text-sm text-destructive">
            {ssoError}
          </p>
        )}

        {!enforced && (
          <form onSubmit={handleSubmit}>
            <label
              htmlFor="email"
              className="mb-1 block text-sm font-medium text-foreground"
            >
              {t("auth.emailLabel")}
            </label>
            <input
              id="email"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("auth.emailPlaceholder")}
              className="mb-4 w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            />

            {error && <p className="mb-4 text-sm text-destructive">{error}</p>}

            <button
              type="submit"
              disabled={loading || isRedirecting || !email.trim()}
              className="w-full rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50"
            >
              {loading ? t("auth.sending") : t("auth.sendMagicLink")}
            </button>
          </form>
        )}

        {SSO_ENABLED && (
          <>
            {!enforced && (
              <div className="my-6 flex items-center gap-3">
                <span className="h-px flex-1 bg-border" />
                <span className="text-xs font-medium uppercase text-muted-foreground">
                  {t("auth.sso.dividerOr")}
                </span>
                <span className="h-px flex-1 bg-border" />
              </div>
            )}

            <div className="space-y-3">
              <SsoProviderButton
                provider="google"
                label={
                  redirectingProvider === "google"
                    ? t("auth.sso.redirecting")
                    : t("auth.sso.continueWithGoogle")
                }
                onClick={() => handleSso("google")}
                disabled={isRedirecting}
                loading={redirectingProvider === "google"}
              />
              <SsoProviderButton
                provider="microsoft"
                label={
                  redirectingProvider === "microsoft"
                    ? t("auth.sso.redirecting")
                    : t("auth.sso.continueWithMicrosoft")
                }
                onClick={() => handleSso("microsoft")}
                disabled={isRedirecting}
                loading={redirectingProvider === "microsoft"}
              />
            </div>
          </>
        )}

        <p className="mt-6 text-center text-xs text-muted-foreground">
          {t("auth.adminOnly")}
        </p>
      </div>
    </div>
  );
}
